import { useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, EmptyState, HeroSelectField, LoadingScreen, PageHeader } from '../components/Ui';
import { useToast } from '../contexts/ToastContext';
import { useBusinessBranches, useBusinessIntelligence } from '../hooks/businessData';
import { formatUsd } from '../lib/currency';
import type {
  BusinessIntelligence,
  IntelligenceBranchComparison,
  IntelligenceTopProduct,
  PreparationDelayBranchComparison,
  PreparationOverrunProduct,
} from '../../shared/types';
import type { TooltipPayloadEntry } from 'recharts';

const ACCENT = '#ff3131';
const GRID_COLOR = '#e2e8f0';
const TICK_COLOR = '#64748b';

const ORDER_STATES_COLORS: Record<string, string> = {
  Completados: '#22c55e',
  Fantasmas: '#f59e0b',
  Cancelados: '#94a3b8',
};

function ageDays(date: Date, days: number): string {
  return new Date(date.getTime() - days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

function todayInputValue(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return 'Sin datos';
  const seconds = Math.max(0, Math.round(ms / 1000));
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return remaining ? `${hours} h ${remaining} min` : `${hours} h`;
}

function formatSignedDuration(ms: number | null | undefined): string {
  if (ms == null) return 'Sin datos';
  const prefix = ms > 0 ? '+' : ms < 0 ? '-' : '';
  return `${prefix}${formatDuration(Math.abs(ms))}`;
}

function formatSeconds(ms: number | null | undefined): string {
  if (ms == null) return '0';
  return String(Math.round((ms ?? 0) / 1000));
}

function formatCount(count: number): string {
  return new Intl.NumberFormat('es').format(count);
}

function formatPercentage(value: number): string {
  return `${new Intl.NumberFormat('es', { minimumFractionDigits: value % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 }).format(value)}%`;
}

export function formatProductQuantity(quantity: number): string {
  return new Intl.NumberFormat('es').format(Math.trunc(quantity));
}

export function formatTopProductLabel(product: IntelligenceTopProduct): string {
  const variant = product.variantName ? ` · ${product.variantName}` : '';
  return `${product.productName}${variant} · ${formatProductQuantity(product.quantity)} unidades`;
}

export function topProductTooltipRows(product: IntelligenceTopProduct): Array<{ name: string; value: string; color?: string }> {
  return [
    { name: 'Unidades vendidas', value: formatProductQuantity(product.quantity), color: '#0ea5e9' },
    { name: 'Ingresos', value: formatUsd(product.revenueUsdCents), color: ACCENT },
  ];
}

function topProductChartData(products: IntelligenceTopProduct[]) {
  return products.map((product) => ({
    ...product,
    label: formatTopProductLabel(product),
  }));
}

function formatUsdAxis(cents: number): string {
  if (cents === 0) return '$0';
  if (cents >= 1_000_000) return `$${(cents / 100_000).toFixed(1)}k`;
  if (cents >= 1000) return `$${Math.round(cents / 100)}`;
  return `$${Math.round(cents / 100)}`;
}

function formatDateAxis(date: string): string {
  const [, month, day] = date.split('-');
  return `${day}/${month}`;
}

function totalRevenueUsdCents(data: BusinessIntelligence): number {
  return data.salesByDay.reduce((sum, row) => sum + row.totalUsdCents, 0);
}

function orderMix(data: BusinessIntelligence): Array<{ name: string; value: number }> {
  return [
    { name: 'Completados', value: data.ticketCount },
    { name: 'Fantasmas', value: data.ghostCount },
    { name: 'Cancelados', value: data.cancelledCount },
  ];
}

function timingByBranch(branches: IntelligenceBranchComparison[]): Array<{
  branchName: string;
  Recepción: number;
  Cocina: number;
}> {
  return branches.map((branch) => ({
    branchName: branch.branchName,
    Recepción: branch.avgReceptionMs ?? 0,
    Cocina: branch.avgKitchenMs ?? 0,
  }));
}

function delayByBranch(branches: PreparationDelayBranchComparison[]) {
  return branches.map((branch) => ({
    branchName: branch.branchName,
    'A tiempo %': branch.onTimePercentage,
    'Con retraso %': branch.delayedPercentage,
  }));
}

function formatPreparationOverrunLabel(product: PreparationOverrunProduct): string {
  const variant = product.variantName ? ` · ${product.variantName}` : '';
  return `${product.productName}${variant}`;
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: readonly TooltipPayloadEntry[];
  label?: string | number;
  labelFormatter?: (label: string | number) => string;
  valueFormatter?: (value: number, entry: TooltipPayloadEntry) => string;
  colorOf?: (name: string) => string;
  extraRows?: Array<{ name: string; value: string; color?: string }>;
}

function ChartTooltip({ active, payload, label, labelFormatter, valueFormatter, colorOf, extraRows = [] }: ChartTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;

  const heading = labelFormatter ? labelFormatter(label ?? '') : String(label ?? '');

  return (
    <div className="min-w-[10rem] rounded-xl border border-slate-200 bg-white p-3 text-sm shadow-lg">
      {heading ? <div className="mb-1.5 font-bold text-slate-800">{heading}</div> : null}
      <div className="grid gap-1">
        {payload.map((entry, index) => {
          const rawName = entry.name ?? String(entry.dataKey ?? '');
          const name = String(rawName);
          const color = (colorOf ? colorOf(name) : undefined) ?? entry.color ?? ACCENT;
          const value =
            valueFormatter && typeof entry.value === 'number'
              ? valueFormatter(entry.value, entry)
              : String(entry.value ?? '');
          return (
            <div key={`${name}-${index}`} className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-1.5 text-slate-500">
                <span className="inline-block size-2.5 rounded-full" style={{ background: color }} />
                {name}
              </span>
              <span className="font-semibold tabular-nums text-slate-800">{value}</span>
            </div>
          );
        })}
        {extraRows.map((row) => (
          <div key={row.name} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-slate-500">
              <span className="inline-block size-2.5 rounded-full" style={{ background: row.color ?? ACCENT }} />
              {row.name}
            </span>
            <span className="font-semibold tabular-nums text-slate-800">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children, className }: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Card className={className}>
      <div>
        <h2 className="text-xl font-black">{title}</h2>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {children}
    </Card>
  );
}

function KpiCard({ icon, label, value, iconClass }: {
  icon: string;
  label: string;
  value: string;
  iconClass: string;
}) {
  return (
    <Card className="min-h-[104px] p-5">
      <div className={iconClass}>{icon}</div>
      <div className="mt-2 text-3xl font-black leading-none">{value}</div>
      <div className="mt-1 text-sm font-semibold leading-tight text-slate-500">{label}</div>
    </Card>
  );
}

export default function BusinessIntelligencePage() {
  const { show } = useToast();
  const now = new Date();
  const [startDate, setStartDate] = useState(ageDays(now, 6));
  const [endDate, setEndDate] = useState(todayInputValue());
  const [branchId, setBranchId] = useState('');

  const { data: branches = [] } = useBusinessBranches();
  const { data, isLoading, isError, error } = useBusinessIntelligence(startDate, endDate, branchId || undefined);

  useEffect(() => {
    if (isError) {
      show(error instanceof Error ? error.message : 'No se pudieron cargar los datos de inteligencia.', 'error');
    }
  }, [error, isError, show]);

  if (isLoading || !data) return <LoadingScreen />;

  const empty = data.salesByDay.length === 0 && data.topProducts.length === 0;

  if (empty) {
    return (
      <div>
        <PageHeader
          title="Inteligencia Operativa"
          description="Ventas, productos y tiempos promedio por rango de fechas."
        />
        <Filters startDate={startDate} endDate={endDate} branchId={branchId} branches={branches}
          onStartDate={setStartDate} onEndDate={setEndDate} onBranchId={setBranchId} />
        <EmptyState
          title="Sin datos en el rango"
          description="No hay pedidos completados, productos ni tiempos para el rango de fechas seleccionado."
        />
      </div>
    );
  }

  const revenue = totalRevenueUsdCents(data);
  const mix = orderMix(data);
  const timingData = timingByBranch(data.branchComparison);
  const delayData = delayByBranch(data.preparationBranchComparison);
  const topProducts = topProductChartData(data.topProducts);

  return (
    <div>
      <PageHeader
        title="Inteligencia Operativa"
        description="Ventas, productos y tiempos promedio por rango de fechas."
      />

      <Filters startDate={startDate} endDate={endDate} branchId={branchId} branches={branches}
        onStartDate={setStartDate} onEndDate={setEndDate} onBranchId={setBranchId} />

      {/* Fila 1: KPIs */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard icon="✓" label="Tickets completados" value={formatCount(data.ticketCount)} iconClass="text-xl text-emerald-600" />
        <KpiCard icon="◌" label="Pedidos fantasma" value={formatCount(data.ghostCount)} iconClass="text-xl text-amber-500" />
        <KpiCard icon="✕" label="Cancelados" value={formatCount(data.cancelledCount)} iconClass="text-xl text-slate-400" />
        <KpiCard icon="$" label="Ingresos totales" value={formatUsd(revenue)} iconClass="text-xl text-black" />
        <KpiCard icon="◷" label="Tareas evaluables" value={formatCount(data.evaluableTaskCount)} iconClass="text-xl text-sky-600" />
        <KpiCard icon="!" label="Tareas con retraso" value={formatCount(data.delayedTaskCount)} iconClass="text-xl text-rose-600" />
        <KpiCard icon="↗" label="A tiempo" value={formatPercentage(data.onTimePercentage)} iconClass="text-xl text-emerald-600" />
        <KpiCard icon="≈" label="Delta promedio real-esperado" value={formatSignedDuration(data.averageDeltaPreparationMs)} iconClass="text-xl text-violet-600" />
      </div>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm">
        Las métricas de preparación solo incluyen productos con tiempo configurado.
      </div>

      {/* Fila 2: Ventas por día (ancho completo) */}
      {data.salesByDay.length > 0 && (
        <div className="mt-6">
          <ChartCard
            title="Ventas por día"
            subtitle="Ingresos y cantidad de pedidos completados por día."
          >
            <div className="mt-4 h-80 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data.salesByDay} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDateAxis}
                    tick={{ fill: TICK_COLOR, fontSize: 12 }}
                    axisLine={{ stroke: GRID_COLOR }}
                    tickLine={false}
                  />
                  <YAxis
                    yAxisId="revenue"
                    tickFormatter={formatUsdAxis}
                    tick={{ fill: TICK_COLOR, fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    width={52}
                  />
                  <YAxis
                    yAxisId="orders"
                    orientation="right"
                    allowDecimals={false}
                    tick={{ fill: TICK_COLOR, fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    width={40}
                  />
                  <Tooltip
                    cursor={{ stroke: GRID_COLOR }}
                    content={({ active, payload, label }) => (
                      <ChartTooltip
                        active={active}
                        payload={payload}
                        label={label}
                        labelFormatter={(l) => `Día ${l}`}
                        valueFormatter={(value, entry) =>
                          entry.dataKey === 'totalUsdCents' ? formatUsd(value) : formatCount(value)
                        }
                        colorOf={(name) => (name === 'Ingresos' ? ACCENT : '#0ea5e9')}
                      />
                    )}
                  />
                  <Legend wrapperStyle={{ fontSize: 13 }} iconType="circle" iconSize={9} />
                  <Bar
                    yAxisId="orders"
                    dataKey="orderCount"
                    name="Pedidos"
                    fill="#0ea5e9"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={34}
                    opacity={0.9}
                  />
                  <Line
                    yAxisId="revenue"
                    type="monotone"
                    dataKey="totalUsdCents"
                    name="Ingresos"
                    stroke={ACCENT}
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: ACCENT }}
                    activeDot={{ r: 5 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        </div>
      )}

      {/* Fila 3: Top productos (barras horizontales) + Mix operativo (donut) */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {topProducts.length > 0 && (
          <ChartCard
            title="Top productos"
            subtitle="Productos más vendidos por ingresos del período."
          >
            <div className="mt-4 h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  layout="vertical"
                  data={topProducts}
                  margin={{ top: 0, right: 24, left: 8, bottom: 0 }}
                  barCategoryGap="22%"
                >
                  <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" horizontal={false} />
                  <XAxis
                    type="number"
                    tickFormatter={formatUsdAxis}
                    tick={{ fill: TICK_COLOR, fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="label"
                    width={220}
                    tick={{ fill: TICK_COLOR, fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: '#f1f5f9' }}
                    content={({ active, payload, label }) => {
                      const product = payload?.[0]?.payload as (IntelligenceTopProduct & { label: string }) | undefined;
                      return (
                        <ChartTooltip
                          active={active}
                          payload={payload}
                          label={label}
                          labelFormatter={(l) => String(l)}
                          valueFormatter={(value) => formatUsd(value)}
                          extraRows={product ? topProductTooltipRows(product) : []}
                        />
                      );
                    }}
                  />
                  <Bar dataKey="revenueUsdCents" name="Ingresos" fill={ACCENT} radius={[0, 6, 6, 0]} maxBarSize={22} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        )}

        <ChartCard
          title="Mix operativo"
          subtitle="Distribución de los estados de los pedidos."
        >
          <div className="mt-4 h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={mix}
                  dataKey="value"
                  nameKey="name"
                  innerRadius="55%"
                  outerRadius="82%"
                  paddingAngle={3}
                  stroke="#fff"
                  strokeWidth={2}
                >
                  {mix.map((entry) => (
                    <Cell key={entry.name} fill={ORDER_STATES_COLORS[entry.name] ?? '#94a3b8'} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => (
                    <ChartTooltip
                      active={active}
                      payload={payload}
                      valueFormatter={(value) => formatCount(value)}
                      colorOf={(name) => ORDER_STATES_COLORS[name] ?? '#94a3b8'}
                    />
                  )}
                />
                <Legend wrapperStyle={{ fontSize: 13 }} iconType="circle" iconSize={9} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>

      {/* Fila 4: Comparativa por sucursal (ancho completo) */}
      {data.branchComparison.length > 0 && (
        <div className="mt-6">
          <ChartCard
            title="Comparativa por sucursal"
            subtitle="Ingresos y pedidos por cada sucursal del período."
          >
            <div className="mt-4 h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.branchComparison} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="branchName"
                    tick={{ fill: TICK_COLOR, fontSize: 12 }}
                    axisLine={{ stroke: GRID_COLOR }}
                    tickLine={false}
                    interval={0}
                  />
                  <YAxis
                    yAxisId="revenue"
                    tickFormatter={formatUsdAxis}
                    tick={{ fill: TICK_COLOR, fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    width={52}
                  />
                  <YAxis
                    yAxisId="orders"
                    orientation="right"
                    allowDecimals={false}
                    tick={{ fill: TICK_COLOR, fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    width={36}
                  />
                  <Tooltip
                    cursor={{ fill: '#f1f5f9' }}
                    content={({ active, payload, label }) => (
                      <ChartTooltip
                        active={active}
                        payload={payload}
                        label={label}
                        labelFormatter={(l) => `Sucursal: ${l}`}
                        valueFormatter={(value, entry) =>
                          entry.dataKey === 'revenueUsdCents' ? formatUsd(value) : formatCount(value)
                        }
                        colorOf={(name) => (name === 'Ingresos' ? ACCENT : '#0ea5e9')}
                      />
                    )}
                  />
                  <Legend wrapperStyle={{ fontSize: 13 }} iconType="circle" iconSize={9} />
                  <Bar
                    yAxisId="revenue"
                    dataKey="revenueUsdCents"
                    name="Ingresos"
                    fill={ACCENT}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={40}
                  />
                  <Bar
                    yAxisId="orders"
                    dataKey="orderCount"
                    name="Pedidos"
                    fill="#0ea5e9"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={40}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        </div>
      )}

      {/* Fila 5: Tiempos promedio por sucursal */}
      {timingData.length > 0 && (
        <div className="mt-6">
          <ChartCard
            title="Tiempos promedio"
            subtitle="Duración media de recepción y cocina por sucursal (pedidos completados)."
          >
            <div className="mt-4 h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={timingData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="branchName"
                    tick={{ fill: TICK_COLOR, fontSize: 12 }}
                    axisLine={{ stroke: GRID_COLOR }}
                    tickLine={false}
                    interval={0}
                  />
                  <YAxis
                    tickFormatter={formatSeconds}
                    tick={{ fill: TICK_COLOR, fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    width={38}
                  />
                  <Tooltip
                    cursor={{ fill: '#f1f5f9' }}
                    content={({ active, payload, label }) => (
                      <ChartTooltip
                        active={active}
                        payload={payload}
                        label={label}
                        labelFormatter={(l) => `Sucursal: ${l}`}
                        valueFormatter={(value) => formatDuration(value)}
                        colorOf={(name) => (name === 'Recepción' ? '#0ea5e9' : '#f59e0b')}
                      />
                    )}
                  />
                  <Legend wrapperStyle={{ fontSize: 13 }} iconType="circle" iconSize={9} />
                  <Bar dataKey="Recepción" fill="#0ea5e9" radius={[4, 4, 0, 0]} maxBarSize={40} />
                  <Bar dataKey="Cocina" fill="#f59e0b" radius={[4, 4, 0, 0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <ChartCard
          title="Salud de preparación"
          subtitle="Indicadores calculados solo con tareas históricas evaluables."
        >
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl bg-slate-50 p-4">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Promedio real</p>
              <p className="mt-2 text-2xl font-black text-slate-950">{formatDuration(data.averageRealPreparationMs)}</p>
            </div>
            <div className="rounded-2xl bg-slate-50 p-4">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Promedio esperado</p>
              <p className="mt-2 text-2xl font-black text-slate-950">{formatDuration(data.averageExpectedPreparationMs)}</p>
            </div>
            <div className="rounded-2xl bg-slate-50 p-4">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">% con retraso</p>
              <p className="mt-2 text-2xl font-black text-slate-950">{formatPercentage(data.delayedPercentage)}</p>
            </div>
            <div className="rounded-2xl bg-slate-50 p-4">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Pedidos demorados por preparación</p>
              <p className="mt-2 text-2xl font-black text-slate-950">{formatCount(data.delayedOrdersByPreparation)}</p>
            </div>
          </div>
        </ChartCard>

        <ChartCard
          title="Productos con mayor sobretiempo"
          subtitle="Ranking por delta promedio entre tiempo real y esperado."
        >
          <div className="mt-4 grid gap-3">
            {data.topPreparationOverruns.length === 0 ? (
              <p className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-500">Sin productos con sobretiempo en el rango seleccionado.</p>
            ) : data.topPreparationOverruns.map((product) => (
              <div key={`${product.productName}-${product.variantName}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-black text-slate-950">{formatPreparationOverrunLabel(product)}</p>
                    <p className="text-sm text-slate-500">{formatCount(product.delayedTaskCount)} retrasadas de {formatCount(product.evaluableTaskCount)} evaluables</p>
                  </div>
                  <p className="text-lg font-black text-rose-600">{formatSignedDuration(product.averageDeltaPreparationMs)}</p>
                </div>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>

      {delayData.length > 0 && (
        <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-[1.2fr_.8fr]">
          <ChartCard
            title="Comparativa de retrasos por sucursal"
            subtitle="Porcentaje de tareas a tiempo vs. con retraso por sucursal."
          >
            <div className="mt-4 h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={delayData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="branchName" tick={{ fill: TICK_COLOR, fontSize: 12 }} axisLine={{ stroke: GRID_COLOR }} tickLine={false} interval={0} />
                  <YAxis tickFormatter={(value) => `${value}%`} tick={{ fill: TICK_COLOR, fontSize: 12 }} axisLine={false} tickLine={false} width={48} />
                  <Tooltip content={({ active, payload, label }) => <ChartTooltip active={active} payload={payload} label={label} labelFormatter={(l) => `Sucursal: ${l}`} valueFormatter={(value) => formatPercentage(value)} colorOf={(name) => (name === 'A tiempo %' ? '#22c55e' : '#ef4444')} />} />
                  <Legend wrapperStyle={{ fontSize: 13 }} iconType="circle" iconSize={9} />
                  <Bar dataKey="A tiempo %" fill="#22c55e" radius={[4, 4, 0, 0]} maxBarSize={40} />
                  <Bar dataKey="Con retraso %" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <ChartCard
            title="Detalle por sucursal"
            subtitle="Pedidos demorados, tareas evaluables y delta promedio."
          >
            <div className="mt-4 grid gap-3">
              {data.preparationBranchComparison.map((branch) => (
                <div key={branch.branchId} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-black text-slate-950">{branch.branchName}</p>
                      <p className="text-sm text-slate-500">{formatCount(branch.delayedOrdersByPreparation)} pedidos demorados · {formatCount(branch.completedOrderCount)} completados</p>
                    </div>
                    <p className="text-sm font-bold text-rose-600">{formatPercentage(branch.delayedPercentage)}</p>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-slate-600">
                    <span>Evaluables: {formatCount(branch.evaluableTaskCount)}</span>
                    <span>Retrasadas: {formatCount(branch.delayedTaskCount)}</span>
                    <span>Real: {formatDuration(branch.averageRealPreparationMs)}</span>
                    <span>Esperado: {formatDuration(branch.averageExpectedPreparationMs)}</span>
                    <span className="col-span-2">Delta: {formatSignedDuration(branch.averageDeltaPreparationMs)}</span>
                  </div>
                </div>
              ))}
            </div>
          </ChartCard>
        </div>
      )}
    </div>
  );
}

function Filters({
  startDate,
  endDate,
  branchId,
  branches,
  onStartDate,
  onEndDate,
  onBranchId,
}: {
  startDate: string;
  endDate: string;
  branchId: string;
  branches: Array<{ id: string; name: string }>;
  onStartDate: (value: string) => void;
  onEndDate: (value: string) => void;
  onBranchId: (value: string) => void;
}) {
  return (
    <div className="mb-6 grid gap-3 sm:grid-cols-3">
      <div className="grid gap-1.5 text-sm font-medium text-foreground">
        <span className="px-0.5">Desde</span>
        <input
          className="h-11 rounded-field border border-border bg-surface px-3 text-sm text-foreground shadow-none outline-none"
          type="date"
          value={startDate}
          onChange={(event) => onStartDate(event.target.value || ageDays(new Date(), 6))}
        />
      </div>
      <div className="grid gap-1.5 text-sm font-medium text-foreground">
        <span className="px-0.5">Hasta</span>
        <input
          className="h-11 rounded-field border border-border bg-surface px-3 text-sm text-foreground shadow-none outline-none"
          type="date"
          value={endDate}
          onChange={(event) => onEndDate(event.target.value || todayInputValue())}
        />
      </div>
      <HeroSelectField
        label="Sucursal"
        value={branchId || null}
        placeholder="Todas"
        onChange={(value) => onBranchId(String(value ?? ''))}
        aria-label="Filtrar por sucursal"
        options={branches.map((branch) => ({ id: branch.id, label: branch.name }))}
      />
    </div>
  );
}
